import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X } from '@phosphor-icons/react';
import { DayPicker } from 'react-day-picker';
import { es } from 'react-day-picker/locale';
import { format, isValid, parse } from 'date-fns';
import 'react-day-picker/style.css';

type Props = {
  label?: string;
  // Fecha en ISO yyyy-MM-dd. La API externa no cambia: el consumidor sigue
  // pasando y recibiendo strings. La conversión a Date pasa adentro.
  value: string;
  onChange: (value: string) => void;
  className?: string;
  error?: boolean;
  errorMessage?: string;
};

const ISO_FORMAT = 'yyyy-MM-dd';
const DISPLAY_FORMAT = 'dd/MM/yyyy';
// Dimensiones aproximadas del DayPicker para decidir el ancla del popup.
// Vertical: ~340px (7 semanas + caption). Horizontal: 7 días × 44px + padding ≈ 320px.
const CALENDAR_APPROX_HEIGHT = 340;
const CALENDAR_APPROX_WIDTH = 320;
// Margen mínimo entre el popup y los bordes del viewport.
const VIEWPORT_MARGIN = 16;
// Gap entre el input y el popup.
const POPUP_GAP = 4;

type PopupPosition = {
  top: number;
  left: number;
};

function isoToDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  // parse interpreta el string en horario local — evita el shift de zona que
  // tendría new Date('2026-05-08') (UTC midnight).
  const d = parse(iso, ISO_FORMAT, new Date());
  return isValid(d) ? d : undefined;
}

export function DateFieldWithClear({
  label,
  value,
  onChange,
  className = '',
  error = false,
  errorMessage,
}: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PopupPosition>({ top: 0, left: 0 });
  // Mide el row del input (sin label / errorMessage). Es lo que importa para
  // decidir dónde anclar el popup.
  const triggerRef = useRef<HTMLDivElement>(null);
  // Como el popup vive en document.body via portal, no está dentro del árbol
  // del componente. Lo necesitamos para que el click-outside no cierre cuando
  // el usuario hace click adentro del calendario (ej. al cambiar de mes).
  const popupRef = useRef<HTMLDivElement>(null);

  const selectedDate = isoToDate(value);
  const displayValue = selectedDate ? format(selectedDate, DISPLAY_FORMAT) : '';

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideTrigger = triggerRef.current?.contains(target);
      const insidePopup = popupRef.current?.contains(target);
      if (!insideTrigger && !insidePopup) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  // Si el usuario scrollea (window o cualquier contenedor con overflow-auto,
  // como el grid del modal), la posición fija del popup queda desfasada del
  // input. Cerramos en ese caso. Capture phase para captar scrolls anidados,
  // ya que el evento "scroll" no burbujea.
  useEffect(() => {
    if (!open) return;
    function handleScroll() {
      setOpen(false);
    }
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  // Calcula coordenadas absolutas del popup en pixels relativos al viewport
  // (porque usamos position: fixed). Decide arriba/abajo y ajusta el eje
  // horizontal para que nunca se salga del viewport por ningún lado.
  function computePosition(): PopupPosition {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove =
      spaceBelow < CALENDAR_APPROX_HEIGHT && spaceAbove > CALENDAR_APPROX_HEIGHT;
    const top = openAbove
      ? rect.top - CALENDAR_APPROX_HEIGHT - POPUP_GAP
      : rect.bottom + POPUP_GAP;

    // Preferencia: anclar al borde izquierdo del input. Si el popup se sale
    // por la derecha, intentamos anclar al borde derecho (popup crece hacia
    // la izquierda). Después, clampeamos contra ambos bordes del viewport
    // para garantizar que entra entero — esto cubre el caso donde el input
    // está cerca del borde derecho del modal pero el modal está cerca del
    // borde izquierdo del viewport (el clamp lo despega del borde izquierdo).
    let left = rect.left;
    if (left + CALENDAR_APPROX_WIDTH > vw - VIEWPORT_MARGIN) {
      left = rect.right - CALENDAR_APPROX_WIDTH;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + CALENDAR_APPROX_WIDTH > vw - VIEWPORT_MARGIN) {
      left = vw - CALENDAR_APPROX_WIDTH - VIEWPORT_MARGIN;
    }

    return { top, left };
  }

  function handleTriggerClick() {
    if (open) {
      setOpen(false);
      return;
    }
    setPosition(computePosition());
    setOpen(true);
  }

  const inputClasses = error
    ? 'w-full bg-stone-100 border-2 border-red-500 rounded px-3 py-2 pr-8 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-300'
    : 'w-full bg-stone-100 border-0 rounded px-3 py-2 pr-8 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green-100';

  function handleSelect(date: Date | undefined) {
    if (date) {
      onChange(format(date, ISO_FORMAT));
      setOpen(false);
    }
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('');
  }

  return (
    <div className={className}>
      {label && (
        <label className="text-xs font-medium text-stone-700 mb-1 block">{label}</label>
      )}
      <div ref={triggerRef} className="relative">
        <input
          type="text"
          value={displayValue}
          readOnly
          placeholder="dd/mm/aaaa"
          onClick={handleTriggerClick}
          aria-invalid={error || undefined}
          className={inputClasses}
        />
        {value ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-stone-200 rounded"
            title="Borrar fecha"
          >
            <X size={12} weight="bold" />
          </button>
        ) : (
          // Solo decorativo: indica que el control es de fecha. pointer-events-none
          // deja pasar el click al input para que abra el popup.
          <Calendar
            size={14}
            weight="bold"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
          />
        )}
      </div>
      {error && errorMessage && (
        <p className="mt-1 text-xs text-red-600">{errorMessage}</p>
      )}
      {open &&
        createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 100, // por encima del modal (z-50)
              // Verde de marca para selección y hoy.
              ['--rdp-accent-color' as string]: '#88A024',
              ['--rdp-accent-background-color' as string]: '#ADD01020',
            }}
            className="bg-white rounded shadow-lg border border-stone-200 p-2"
          >
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              locale={es}
              weekStartsOn={1}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
